<template>
  <v-container fluid>
    <v-row class="d-flex flex-row">
      <v-col cols="12">
        <FormPageTouristAgency
          :data="eventOrganizer"
          :cities="cities"
          :shared-access="sharedAccess"
          :ticket-reservations="ticketReservations"
          :loading="isFormLoading"
          :disabled="isMutationLoading"
          :isNew="!id"
          :language="language"
          @submit="formEventOraganizerSubmitHandler"
          @delete-event-organizer="deleteEventOrganizer"
          @language-update="language = $event"
          @change:descriptionForMobile="changeDescForMobHandler($event)"
        />
      </v-col>
    </v-row>
    <ConfirmationDialog ref="confirmEventOrganizerEmoving" confirmText="Удалить" />
  </v-container>
</template>

<script>
import { contentLocales, contentLocalesValues } from '@/configs/locales'
import { getListWithNewMainLanguage, setTranslation } from '@/utils/locales'

import FormPageTouristAgency from './form'
import ConfirmationDialog from '@/components/confirmation-dialog'

import citiesQuery from '@/services/gql/types/City/queries/citiesGrid.gql'
import eventOrganizerQuery from '@/services/gql/types/EventOrganizers/queries/eventOrganizer.gql'
import sharedAccessQuery from '@/services/gql/types/SharedAccess/queries/sharedAccess.gql'
import ticketReservationsOfRelations from '@/services/gql/types/TicketReservations/queries/ticketReservationsOfRelations.gql'
import createEventOrganizer from '@/services/gql/types/EventOrganizers/mutations/createEventOrganizer.gql'
import updateEventOrganizery from '@/services/gql/types/EventOrganizers/mutations/updateEventOrganizer.gql'
import deleteEventOrganizer from '@/services/gql/types/EventOrganizers/mutations/deleteEventOrganizer.gql'

export default {
  name: 'PageEventOrganizer',
  props: {
    id: {
      type: [String, Number],
      default: null
    }
  },
  components: {
    FormPageTouristAgency,
    ConfirmationDialog
  },
  metaInfo () {
    return {
      title: this.id ? 'Редактировать организатора мероприятий' : 'Создать организатора мероприятий'
    }
  },
  data () {
    return {
      language: contentLocales[0].value,
      cities: null,
      eventOrganizer: null,
      sharedAccess: null,
      ticketReservations: null
    }
  },
  computed: {
    isMutationLoading () {
      return this.$async.createEventOrganizer.$pending || this.$async.updateEventOrganizer.$pending
    },
    isFormLoading () {
      return (
        this.$apollo.queries.eventOrganizer.loading ||
        this.$apollo.queries.cities.loading
      )
    }
  },
  methods: {
    changeDescForMobHandler ({ fields, $event }) {
      fields.descriptionForMobile = $event
    },
    async formEventOraganizerSubmitHandler (eventOrganizerPayload) {
      if (!this.id) await this.createEventOrganizer(eventOrganizerPayload)
      else await this.updateEventOrganizer(eventOrganizerPayload)
    },
    async createEventOrganizer ({ data }) {
      delete data.translations
      try {
        await this.$async.createEventOrganizer.$perform({
          data
        })
        this.$successMsg('Организатор мероприятий создан')
        await this.$router.push({ name: 'page-event-organizers' })
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при создании организатора мероприятий')
      }
    },
    async deleteEventOrganizer () {
      try {
        await this.$refs.confirmEventOrganizerEmoving.open()
        await this.$async.deleteEventOrganizer.$perform({ id: Number(this.id) })
        this.$successMsg('Организатор мероприятий удален')
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, `Ошибка при удалении организатора мероприятий с ID ${this.id}`)
      } finally {
        await this.$router.push({ name: 'page-event-organizers' })
      }
    },
    async updateEventOrganizer ({ data }) {
      try {
        setTranslation(data, this.language)
        await this.$async.updateEventOrganizer.$perform(
          {
            id: Number(this.id),
            data
          },
          { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) }
        )
        this.$successMsg('Организатор мероприятия обновлен')
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при редактировании организатора мероприятия')
      }
    }
  },
  apollo: {
    eventOrganizer: {
      query: eventOrganizerQuery,
      fetchPolicy: 'network-only',
      skip () {
        return !this.id
      },
      variables () {
        return {
          where: {
            id: Number(this.id)
          }
        }
      },
      context () {
        return { headers: { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) } }
      },
      result (res) {
        if (res.data === undefined) {
          this.$handleApiError(res.error)
          this.$router.push({ name: 'page-tourist-agencies' })
        } else {
          this.touristAgency = res?.data?.touristAgency || null
        }
      }
    },
    cities: {
      query: citiesQuery,
      fetchPolicy: 'network-only',
      pollInterval: 30000,
      variables: {
        where: {
          status_not: 'DRAFT'
        }
      },
      result (res) {
        this.cities = res?.data?.cities?.list || null
      }
    },
    sharedAccess: {
      query: sharedAccessQuery,
      fetchPolicy: 'network-only',
      skip () {
        return !this.id
      },
      variables () {
        return {
          where: {
            profile: {
              id: Number(this.id)
            }
          }
        }
      },
      context () { return { headers: { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) } } },
      result (res) {
        this.sharedAccess = res?.data?.sharedAccess?.list
      }
    },
    ticketReservations: {
      query: ticketReservationsOfRelations,
      fetchPolicy: 'network-only',
      skip () {
        return !this.id
      },
      variables () {
        return {
          where: {
            profileType: 'EVENT_ORGANIZER',
            profileId: Number(this.id)
          }
        }
      },
      context () { return { headers: { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) } } },
      result (res) {
        const { list } = res?.data?.ticketReservations
        const tickets = list
          ?.map(ticket => ({ count: 1, name: ticket.displayName }))
          ?.reduce((acc, ticket) => {
            acc[ticket.name] = (acc[ticket.name] || 0) + ticket.count
            return acc
          }, {}) || null
        this.ticketReservations = Object.keys(tickets)?.map(ticket => ({
          title: ticket + ': ' + tickets[ticket]
        }))
      }
    }
  },
  async: {
    createEventOrganizer (variables) {
      return this.$apollo.mutate({
        mutation: createEventOrganizer,
        variables
      })
    },
    deleteEventOrganizer (variables) {
      return this.$apollo.mutate({
        mutation: deleteEventOrganizer,
        variables
      })
    },
    updateEventOrganizer (variables, headers) {
      return this.$apollo.mutate({
        mutation: updateEventOrganizery,
        variables,
        context: {
          headers
        }
      })
    }
  }
}
</script>
